import type { BigSource } from 'big.js';
import Big from 'big.js';
import { useCallback, useMemo, useState } from 'react';

import type { Security } from '@talos/kyoko';
import {
  ConnectionStatusEnum,
  ModeEnum,
  Text,
  format,
  isDelta1Multileg,
  logger,
  useConversionValue,
  useDisclosure,
  useObservableValue,
} from '@talos/kyoko';

import { useCurrenciesContext } from '@src/providers';
import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { useTradingLimits } from '@src/providers/TradingLimitsContext';

export type MaxOrderSizeValidationState = 'idle' | 'clean' | 'warn' | 'reject' | 'loading';

export const useMaxOrderSizeDialogs = ({
  currency,
  onValidationStateChange,
  symbol,
  onConfirmWarning,
  security,
}: {
  currency: string | undefined;
  onValidationStateChange?: (validationState: MaxOrderSizeValidationState) => void;
  symbol: string | undefined;
  onConfirmWarning: () => void;
  security: Security | undefined;
}) => {
  const limits = useTradingLimits();
  const [warnMessage, setWarnMessage] = useState<React.ReactElement | string | undefined>();
  const [rejectMessage, setRejectMessage] = useState<React.ReactElement | string | undefined>();
  const [validationState, setValidationState] = useState<MaxOrderSizeValidationState>('idle');
  const { homeCurrency } = useDisplaySettings();

  const warnDialog = useDisclosure();
  const warnDialogProps = useMemo(
    () => ({
      usePortal: false,
      cancelLabel: 'No',
      confirmLabel: 'Yes',
      onConfirm: onConfirmWarning,
      onCancel: () => warnDialog.close(),
      close: warnDialog.close,
      isOpen: warnDialog.isOpen,
    }),
    [warnDialog, onConfirmWarning]
  );

  const rejectDialog = useDisclosure();
  const rejectDialogProps = useMemo(
    () => ({
      usePortal: false,
      showConfirm: false,
      cancelLabel: 'OK',
      close: rejectDialog.close,
      isOpen: rejectDialog.isOpen,
    }),
    [rejectDialog]
  );

  const { currenciesBySymbol } = useCurrenciesContext();
  const conversion = useConversionValue(currency, symbol, security);
  const openRejectDialog = rejectDialog.open;
  const openWarnDialog = warnDialog.open;
  const closeRejectDialog = rejectDialog.close;
  const closeWarnDialog = warnDialog.close;
  const maxOrderSize = useObservableValue(() => limits.maxOrderSizeByKey, [limits.maxOrderSizeByKey]);
  const setReportedValidationState = useCallback(
    (state: MaxOrderSizeValidationState) => {
      setValidationState(state);
      onValidationStateChange?.(state);
    },
    [onValidationStateChange]
  );
  const validate = useCallback(
    ({
      size,
      checkWarn = true,
      checkReject = true,
    }: {
      size: string | undefined;
      checkWarn?: boolean;
      checkReject?: boolean;
    }): true | false | 'loading' | undefined => {
      closeWarnDialog();
      closeRejectDialog();
      try {
        if (size == null) {
          throw new Error('Max Order Size validation failed. Order size is missing.');
        }
        if (maxOrderSize == null) {
          setReportedValidationState('loading');
          return 'loading';
        }
        const hasEnabledLimits = maxOrderSize.size && [...maxOrderSize.values()].some(m => m.Mode === ModeEnum.Enabled);
        if (!hasEnabledLimits) {
          setReportedValidationState('clean');
          return true;
        }

        // https://talostrading.atlassian.net/browse/UI-3582
        if (isDelta1Multileg(security)) {
          setReportedValidationState('clean');
          return true;
        }

        if (conversion == null || conversion.Status === ConnectionStatusEnum.Unavailable) {
          logger.warn(`Missing conversion rate for ${currency} while validating max order size`, {
            extra: {
              conversion: JSON.stringify(conversion),
            },
          });
          setReportedValidationState('loading');
          return 'loading';
        }

        let validationResult;
        try {
          const sizeHomeCurrency = Big(size).times(conversion?.Rate || '1');
          validationResult = limits.validateMaxOrderSize(sizeHomeCurrency, symbol);
        } catch (error) {
          // If validation fails due to missing threshold currency conversions, log and allow order
          logger.error(error as Error, {
            extra: {
              context:
                'Max order size validation threw error. Either missing threshold currency or failed to initialize',
              orderCurrency: currency,
              symbol,
            },
          });
          // Allow the order to proceed - backend will validate
          setReportedValidationState('clean');
          return true;
        }

        const { warn, reject, limit } = validationResult;

        const formatValue = (value: BigSource, currency: string | undefined) => {
          const currencyDetails = currency ? currenciesBySymbol.get(currency) : undefined;
          return currencyDetails == null
            ? value.toString()
            : format(value, {
                spec: currencyDetails.DefaultIncrement,
                truncate: true,
              });
        };

        const formatToHomeCurrency = (value: BigSource, valueCurrency: string | undefined) => {
          let conversionRate;
          if (currency === valueCurrency) {
            conversionRate = conversion?.Rate;
          } else {
            conversionRate = valueCurrency ? limits.conversionRates.get(valueCurrency)?.Rate : undefined;
          }
          if (conversionRate) {
            return `(${formatValue(Big(conversionRate).times(value), homeCurrency)} ${homeCurrency})`;
          }
          logger.error(
            new Error(
              `Missing conversion rate for ${currency} to ${valueCurrency} while formatting max order size dialog`
            ),
            {
              extra: {
                conversionRate: JSON.stringify(conversionRate),
              },
            }
          );
          throw new Error(`Missing conversion rate for ${valueCurrency}. Please contact Talos support.`);
        };

        if (checkReject && reject && limit) {
          setRejectMessage(
            <p>
              Order size{' '}
              <Text color="colorTextImportant">
                {formatValue(size, currency)} {currency}
              </Text>{' '}
              {currency !== homeCurrency && formatToHomeCurrency(size, currency)} is greater than
              {limit.Description && <Text color="colorTextImportant"> {limit.Description}</Text>} rejection threshold{' '}
              <Text color="colorTextImportant">
                {formatValue(limit.RejectThreshold, limit.ThresholdCurrency)} {limit.ThresholdCurrency}
              </Text>{' '}
              {limit.ThresholdCurrency !== homeCurrency &&
                formatToHomeCurrency(limit.RejectThreshold, limit.ThresholdCurrency)}
              .
            </p>
          );
          openRejectDialog();
          setReportedValidationState('reject');
          return false;
        }

        if (checkWarn && warn && limit) {
          setWarnMessage(
            <>
              <p>
                Order size{' '}
                <Text color="colorTextImportant">
                  {formatValue(size, currency)} {currency}
                </Text>{' '}
                {currency !== homeCurrency && formatToHomeCurrency(size, currency)} is greater than
                {limit.Description && <Text color="colorTextImportant"> {limit.Description}</Text>} warning threshold{' '}
                <Text color="colorTextImportant">
                  {formatValue(limit.WarnThreshold, limit.ThresholdCurrency)} {limit.ThresholdCurrency}
                </Text>{' '}
                {limit.ThresholdCurrency !== homeCurrency &&
                  formatToHomeCurrency(limit.WarnThreshold, limit.ThresholdCurrency)}
                .
              </p>{' '}
              <p>Are you sure you want to proceed?</p>
            </>
          );
          openWarnDialog();
          setReportedValidationState('warn');
          return false;
        }
      } catch (e: any) {
        setRejectMessage(e.message as string);
        openRejectDialog();
        setReportedValidationState('reject');
        return false;
      }

      setReportedValidationState('clean');
      return true;
    },
    [
      closeWarnDialog,
      closeRejectDialog,
      maxOrderSize,
      conversion,
      limits,
      symbol,
      security,
      currency,
      openRejectDialog,
      openWarnDialog,
      currenciesBySymbol,
      homeCurrency,
      setReportedValidationState,
    ]
  );

  return {
    warnDialog: warnDialogProps,
    warnMessage,
    rejectDialog: rejectDialogProps,
    rejectMessage,
    validate,
    validationState,
  };
};
